// Recruitment SEO copy and the single confirmed job posting.
// Only verified data is used here: unknown fields (salary, exact location, employment type,
// full description, dates) are never invented — they read "details given during the interview".
// This file is the single source of truth for the careers page copy, the JobPosting JSON-LD
// in the app head (components/Seo.jsx) and the static prerendered HTML (scripts/prerender.js).
import { CANONICAL_HOST } from "@/i18n/seo";
import { routeSlug } from "@/i18n/routes";

export const CAREERS_SALES = {
  fr: {
    h1: "Nos offres d'emploi dans la vente",
    intro:
      "Consultants Sparks recrute des agents et agentes des ventes pour ses campagnes commerciales en magasin, au Québec. Une seule candidature suffit pour amorcer la conversation, et le CV est facultatif.",
    salesTitle: "Devenez vendeur / vendeuse chez Consultants Sparks",
    salesBody: [
      "Nos équipes réunissent des vendeurs et vendeuses, des conseillers et conseillères aux ventes ainsi que des représentants et représentantes des ventes qui travaillent en magasin, au contact direct des clients.",
      "Chaque poste de vendeur est encadré : vous commencez avec un accompagnement structuré plutôt qu'un apprentissage laissé au hasard.",
    ],
    whyTitle: "Pourquoi choisir une carrière en vente avec nous",
    whyPoints: [
      "Un emploi dans la vente en magasin, au sein de campagnes commerciales concrètes, dont la campagne Canadian Tire.",
      "Un accompagnement structuré dès la première journée, pour les profils débutants comme expérimentés.",
      "Une progression claire : représentant ou représentante des ventes, puis responsabilités d'encadrement.",
      "Une candidature simple : quelques minutes en ligne, CV facultatif.",
    ],
    offerTitle: "Poste affiché : agent / agente des ventes",
    offerRows: [
      ["Titre du poste", "Agent / Agente des ventes"],
      ["Organisation", "Consultants Sparks"],
      ["Nature du poste", "Vente en magasin, campagnes commerciales (dont la campagne Canadian Tire)"],
      ["Référence", "Affichage Québec Emploi nº 546019"],
      ["Salaire", "Détails précisés lors de l'entrevue"],
      ["Lieu de travail", "Détails précisés lors de l'entrevue"],
      ["Type d'emploi", "Détails précisés lors de l'entrevue"],
      ["Description complète", "Détails précisés lors de l'entrevue"],
    ],
    applyCta: "Postuler à un emploi dans la vente",
    faqTitle: "Questions fréquentes sur nos emplois en vente",
    faq: [
      {
        q: "Comment postuler à un emploi de vendeur ?",
        a: "Remplissez le formulaire de candidature en ligne sur consultantsparks.com/fr/candidature. Le CV est facultatif et un membre de notre équipe communique ensuite avec vous.",
      },
      {
        q: "Quels postes en vente sont disponibles ?",
        a: "Le poste affiché est celui d'agent / agente des ventes, en vente en magasin dans le cadre de nos campagnes commerciales (affichage Québec Emploi nº 546019). Les autres détails sont précisés lors de l'entrevue.",
      },
      {
        q: "Faut-il de l'expérience en vente pour appliquer ?",
        a: "Aucune expérience préalable n'est exigée pour soumettre votre candidature. Les profils débutants comme expérimentés en vente sont évalués de la même façon et un accompagnement structuré est prévu.",
      },
    ],
  },
  en: {
    h1: "Our sales job openings",
    intro:
      "Consultants Sparks is hiring sales agents for its in-store sales campaigns in Québec. One general application is enough to start the conversation, and the CV is optional.",
    salesTitle: "Become a salesperson at Consultants Sparks",
    salesBody: [
      "Our teams bring together salespeople, sales advisors and sales representatives who work in store, in direct contact with customers.",
      "Every sales position is supported: you start with structured guidance rather than learning left to chance.",
    ],
    whyTitle: "Why choose a sales career with us",
    whyPoints: [
      "An in-store sales job within concrete commercial campaigns, including the Canadian Tire campaign.",
      "Structured support from day one, for beginners and experienced sales profiles alike.",
      "A clear path: sales representative, then team supervision responsibilities.",
      "A simple application: a few minutes online, CV optional.",
    ],
    offerTitle: "Posting: sales agent",
    offerRows: [
      ["Job title", "Sales agent"],
      ["Organization", "Consultants Sparks"],
      ["Nature of the role", "In-store sales, commercial campaigns (including the Canadian Tire campaign)"],
      ["Reference", "Québec Emploi posting no. 546019"],
      ["Salary", "Details given during the interview"],
      ["Work location", "Details given during the interview"],
      ["Employment type", "Details given during the interview"],
      ["Full description", "Details given during the interview"],
    ],
    applyCta: "Apply for a sales job",
    faqTitle: "Frequently asked questions about our sales jobs",
    faq: [
      {
        q: "How do I apply for a salesperson job?",
        a: "Fill in the online application form at consultantsparks.com/en/apply. The CV is optional and a member of our team then contacts you.",
      },
      {
        q: "Which sales positions are available?",
        a: "The posted position is sales agent, in-store sales within our commercial campaigns (Québec Emploi posting no. 546019). Remaining details are given during the interview.",
      },
      {
        q: "Do I need sales experience to apply?",
        a: "No prior experience is required to submit your application. Beginner and experienced sales profiles are assessed the same way and structured support is provided.",
      },
    ],
  },
  es: {
    h1: "Nuestras ofertas de empleo en ventas",
    intro:
      "Consultants Sparks contrata agentes de ventas para sus campañas comerciales en tienda, en Quebec. Una sola candidatura basta para iniciar la conversación y el CV es opcional.",
    salesTitle: "Conviértase en vendedor o vendedora en Consultants Sparks",
    salesBody: [
      "Nuestros equipos reúnen vendedores y vendedoras, asesores y asesoras de ventas y representantes de ventas que trabajan en tienda, en contacto directo con los clientes.",
      "Cada puesto de vendedor está acompañado: usted comienza con un acompañamiento estructurado, no con un aprendizaje dejado al azar.",
    ],
    whyTitle: "Por qué elegir una carrera en ventas con nosotros",
    whyPoints: [
      "Un empleo en ventas en tienda, dentro de campañas comerciales concretas, incluida la campaña Canadian Tire.",
      "Acompañamiento estructurado desde el primer día, tanto para perfiles principiantes como experimentados.",
      "Una progresión clara: representante de ventas y después responsabilidades de supervisión.",
      "Una candidatura sencilla: unos minutos en línea, CV opcional.",
    ],
    offerTitle: "Puesto publicado: agente de ventas",
    offerRows: [
      ["Título del puesto", "Agente de ventas"],
      ["Organización", "Consultants Sparks"],
      ["Naturaleza del puesto", "Ventas en tienda, campañas comerciales (incluida la campaña Canadian Tire)"],
      ["Referencia", "Publicación Québec Emploi n.º 546019"],
      ["Salario", "Detalles precisados durante la entrevista"],
      ["Lugar de trabajo", "Detalles precisados durante la entrevista"],
      ["Tipo de empleo", "Detalles precisados durante la entrevista"],
      ["Descripción completa", "Detalles precisados durante la entrevista"],
    ],
    applyCta: "Postular a un empleo en ventas",
    faqTitle: "Preguntas frecuentes sobre nuestros empleos en ventas",
    faq: [
      {
        q: "¿Cómo postular a un empleo de vendedor?",
        a: "Complete el formulario de candidatura en línea en consultantsparks.com/es/candidatura. El CV es opcional y un miembro de nuestro equipo se pone luego en contacto con usted.",
      },
      {
        q: "¿Qué puestos en ventas están disponibles?",
        a: "El puesto publicado es agente de ventas, en ventas en tienda dentro de nuestras campañas comerciales (publicación Québec Emploi n.º 546019). Los demás detalles se precisan durante la entrevista.",
      },
      {
        q: "¿Se necesita experiencia en ventas para postular?",
        a: "No se exige experiencia previa para enviar su candidatura. Los perfiles principiantes y experimentados en ventas se evalúan de la misma manera y se prevé un acompañamiento estructurado.",
      },
    ],
  },
};

export const JOB_POSTING = {
  identifierName: "Québec Emploi",
  identifierValue: "546019",
  fr: {
    title: "Agent / Agente des ventes",
    description:
      "Agent / agente des ventes chez Consultants Sparks : vente en magasin dans le cadre de campagnes commerciales, dont la campagne Canadian Tire. Salaire, lieu de travail exact, type d'emploi et description complète : détails précisés lors de l'entrevue. Référence : affichage Québec Emploi nº 546019. Candidature en ligne, CV facultatif.",
  },
  en: {
    title: "Sales agent",
    description:
      "Sales agent at Consultants Sparks: in-store sales within commercial campaigns, including the Canadian Tire campaign. Salary, exact work location, employment type and full description: details given during the interview. Reference: Québec Emploi posting no. 546019. Online application, CV optional.",
  },
  es: {
    title: "Agente de ventas",
    description:
      "Agente de ventas en Consultants Sparks: ventas en tienda dentro de campañas comerciales, incluida la campaña Canadian Tire. Salario, lugar de trabajo exacto, tipo de empleo y descripción completa: detalles precisados durante la entrevista. Referencia: publicación Québec Emploi n.º 546019. Candidatura en línea, CV opcional.",
  },
};

/** JobPosting JSON-LD. Unknown fields are omitted rather than invented. */
export function jobPostingLd(lang) {
  const copy = JOB_POSTING[lang] || JOB_POSTING.fr;
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: copy.title,
    description: copy.description,
    identifier: {
      "@type": "PropertyValue",
      name: JOB_POSTING.identifierName,
      value: JOB_POSTING.identifierValue,
    },
    hiringOrganization: {
      "@type": "Organization",
      name: "Consultants Sparks",
      url: CANONICAL_HOST,
      sameAs: CANONICAL_HOST,
    },
    directApply: true,
    url: `${CANONICAL_HOST}/${lang}${routeSlug("apply", lang)}`,
  };
}
